import { useEffect, useRef, useState } from "react";
import { trpc } from "../../_core/trpc";
import { Panel } from "./Admin";
import { TIMEZONES, formatInZone, zoneLabel, wallClockOf } from "@/shared/birthday";
import { Countdown } from "../../components/lux/Countdown";
function AdminSchedule() {
  const overview = trpc.admin.overview.useQuery();
  const settings = trpc.admin.getSettings.useQuery();
  const save = trpc.admin.saveSettings.useMutation();
  const utils = trpc.useUtils();
  const [date, setDate] = useState("2026-12-12");
  const [time, setTime] = useState("00:00");
  const [tz, setTz] = useState("Asia/Kolkata");
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const hydrated = useRef(false);
  useEffect(() => {
    if (!settings.data || hydrated.current) return;
    hydrated.current = true;
    const t = new Date(settings.data.birthdayAt);
    const wc = wallClockOf(t, settings.data.timezone);
    setDate(`${wc.year}-${String(wc.month).padStart(2, "0")}-${String(wc.day).padStart(2, "0")}`);
    setTime(`${String(wc.hour).padStart(2, "0")}:${String(wc.minute).padStart(2, "0")}`);
    setTz(settings.data.timezone);
  }, [settings.data]);
  const previewQ = trpc.admin.preview.useQuery(
    { as: preview ?? "locked" },
    { enabled: preview !== null }
  );
  const onSave = async () => {
    setStatus(null);
    const [y, m, d] = date.split("-").map(Number);
    const [hh, mm] = time.split(":").map(Number);
    if (!y || !m || !d) {
      setStatus({ kind: "err", msg: "Please pick a date." });
      return;
    }
    try {
      const res = await save.mutateAsync({
        timezone: tz,
        birthday: { year: y, month: m, day: d, hour: hh || 0, minute: mm || 0 }
      });
      setStatus({
        kind: "ok",
        msg: `Saved. Unlocks at ${formatInZone(new Date(res.birthdayAt), tz)} (${tz}).`
      });
      await utils.admin.getSettings.invalidate();
      await utils.admin.overview.invalidate();
      await utils.public.state.invalidate();
    } catch (e) {
      setStatus({ kind: "err", msg: e instanceof Error ? e.message : "Save failed." });
    }
  };
  const savedAt = overview.data ? new Date(overview.data.birthdayAt) : null;
  return <Panel
    eyebrow="Midnight"
    title="When her world opens"
    description="Choose the exact minute her birthday begins. The site switches over on its own at that moment — no refresh, no manual step."
    aside={overview.data && <span className={`pill ${overview.data.unlocked ? "pill-live" : "pill-locked"}`}>
            {overview.data.unlocked ? "unlocked now" : "locked now"}
          </span>}
  >
      {status && <div
    role="status"
    className={`mb-5 rounded-xl border px-4 py-3 text-sm ${status.kind === "ok" ? "border-[rgba(126,217,154,.35)] bg-[rgba(38,120,66,.14)] text-[#a8e6bd]" : "border-[rgba(244,63,94,.4)] bg-[rgba(244,63,94,.14)] text-[#ffc9cd]"}`}
  >
          {status.msg}
        </div>}

      <div className="grid gap-5 lg:grid-cols-2">
        {
    /* ── the form ── */
  }
        <div className="glass rounded-xl p-5">
          <p className="eyebrow mb-4">The moment</p>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1.5 block text-xs uppercase tracking-[0.16em] text-[#c9cce0]">Date</span>
              <input
    type="date"
    className="field-lux"
    value={date}
    onChange={(e) => setDate(e.target.value)}
    aria-label="Birthday date"
  />
            </label>
            <label className="block">
              <span className="mb-1.5 block text-xs uppercase tracking-[0.16em] text-[#c9cce0]">Time</span>
              <input
    type="time"
    className="field-lux"
    value={time}
    onChange={(e) => setTime(e.target.value)}
    aria-label="Unlock time"
  />
            </label>
          </div>

          <label className="mt-4 block">
            <span className="mb-1.5 block text-xs uppercase tracking-[0.16em] text-[#c9cce0]">
              Timezone
            </span>
            <select
    className="field-lux"
    value={tz}
    onChange={(e) => setTz(e.target.value)}
    aria-label="Timezone for the birthday"
  >
              {TIMEZONES.map((z) => <option key={z} value={z}>
                  {z}
                </option>)}
            </select>
            <span className="mt-1.5 block text-[0.68rem] text-[color:var(--muted-foreground)]">
              Midnight is computed in this timezone. Her device&apos;s timezone does not matter — the
              unlock is the same moment for everyone.
            </span>
          </label>

          <button type="button" onClick={onSave} className="btn-gold mt-5 w-full" disabled={save.isPending}>
            {save.isPending ? "Saving\u2026" : "Save the moment"}
          </button>

          {savedAt && <div className="mt-4 rounded-lg border border-[rgba(233,168,242,.16)] bg-white/[0.02] px-3 py-2.5 text-[0.72rem] leading-relaxed text-[color:var(--muted-foreground)]">
              <p>
                <span className="text-[#dcdff0]">Currently set to</span>{" "}
                {formatInZone(savedAt, settings.data?.timezone ?? tz)} ·{" "}
                {zoneLabel(savedAt, settings.data?.timezone ?? tz)}
              </p>
              <p className="mt-1 tabular-nums">
                Stored instant: {savedAt.toISOString()} — this is what the server compares against.
              </p>
            </div>}
        </div>

        {
    /* ── live countdown against the SAVED moment ── */
  }
        <div className="glass rounded-xl p-5">
          <p className="eyebrow mb-4">Live countdown (saved schedule)</p>
          {overview.data ? overview.data.unlocked ? <div className="py-6 text-center">
                <p className="display-md gold-text">The moment has passed</p>
                <p className="mt-2 text-sm text-[color:var(--muted-foreground)]">
                  Anyone opening her link now sees the full experience straight away.
                </p>
              </div> : <Countdown
    targetIso={overview.data.birthdayAt}
    serverNowIso={overview.data.now}
    compact
  /> : <p className="text-sm text-[color:var(--muted-foreground)]">Loading…</p>}

          <div className="gold-rule my-5" />

          <p className="eyebrow mb-3">Test it</p>
          <p className="mb-3 text-[0.74rem] leading-relaxed text-[color:var(--muted-foreground)]">
            Opens the real page in a preview frame. Nothing about the schedule changes — this is a
            read-only look at what she would see right now.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
    type="button"
    onClick={() => setPreview("locked")}
    className={`btn-ghost-lux !min-h-[38px] !px-4 !text-[0.64rem] ${preview === "locked" ? "!border-[rgba(233,168,242,.6)] !bg-[rgba(233,168,242,.12)]" : ""}`}
  >
              Preview locked
            </button>
            <button
    type="button"
    onClick={() => setPreview("unlocked")}
    className={`btn-ghost-lux !min-h-[38px] !px-4 !text-[0.64rem] ${preview === "unlocked" ? "!border-[rgba(233,168,242,.6)] !bg-[rgba(233,168,242,.12)]" : ""}`}
  >
              Preview unlocked
            </button>
            {preview && <button
    type="button"
    onClick={() => setPreview(null)}
    className="px-2 text-[0.62rem] uppercase tracking-[0.14em] text-[color:var(--muted-foreground)] hover:text-[#e9a8f2]"
  >
                Close
              </button>}
          </div>
        </div>
      </div>

      {
    /* ── preview panel ── */
  }
      {preview && <div className="glass mt-5 overflow-hidden rounded-xl">
          <div className="flex items-center justify-between border-b border-[rgba(233,168,242,.14)] px-4 py-2.5">
            <p className="eyebrow">
              {preview === "locked" ? "Locked \u2014 what she sees before midnight" : "Unlocked \u2014 the birthday experience"}
            </p>
            <span className="text-[0.6rem] uppercase tracking-[0.14em] text-[color:var(--muted-foreground)]">
              preview only
            </span>
          </div>

          {previewQ.isLoading && <p className="px-4 py-8 text-center text-sm text-[color:var(--muted-foreground)]">Loading…</p>}

          {previewQ.error && <p className="px-4 py-8 text-center text-sm text-[#ffc9cd]">
              {previewQ.error.message}
            </p>}

          {previewQ.data && <div className="max-h-[560px] overflow-y-auto bg-[#0b0c16] p-6">
              {previewQ.data.unlocked ? <div className="mx-auto max-w-xl text-center">
                  <p className="eyebrow">{previewQ.data.subtitles[0] ?? "Today is yours"}</p>
                  <h3 className="display-lg gold-text mt-3">{previewQ.data.mainHeading}</h3>
                  <div className="gold-rule my-5" />
                  <p className="prose-romance whitespace-pre-line text-left text-[0.92rem]">
                    {previewQ.data.letterBody}
                  </p>
                  <div className="mt-6 grid grid-cols-3 gap-2 text-center">
                    <PreviewStat label="Photos" value={previewQ.data.photos.length} />
                    <PreviewStat label="Videos" value={previewQ.data.videos.length} />
                    <PreviewStat label="Tracks" value={previewQ.data.audio.length} />
                  </div>
                  <p className="mt-5 text-[0.68rem] uppercase tracking-[0.16em] text-[color:var(--muted-foreground)]">
                    {previewQ.data.messages.length} message(s) available after midnight
                  </p>
                </div> : <div className="mx-auto max-w-xl text-center">
                  <p className="eyebrow">A private little world</p>
                  <h3 className="display-md gold-text mt-3">{previewQ.data.teaserText}</h3>
                  <div className="mt-6">
                    <Countdown
    targetIso={previewQ.data.birthdayAt}
    serverNowIso={previewQ.data.now}
    compact
  />
                  </div>
                  <p className="mt-4 text-[0.7rem] text-[color:var(--muted-foreground)]">
                    {previewQ.data.teaserMedia.length} teaser item(s) visible · letter and gallery
                    withheld by the server
                  </p>
                </div>}
            </div>}
        </div>}

      <div className="glass mt-5 rounded-xl p-5">
        <p className="eyebrow mb-2">How the unlock is enforced</p>
        <ul className="space-y-1.5 text-[0.78rem] leading-relaxed text-[color:var(--muted-foreground)]">
          <li>
            • The letter, gallery, video, song and hidden notes are only returned by the server once
            the stored instant has passed. Before then they are not in any response at all — not
            hidden with CSS.
          </li>
          <li>
            • The page checks with the server when the countdown reaches zero, then reveals without a
            refresh.
          </li>
          <li>
            • Opening the link any time after midnight shows the unlocked experience immediately.
          </li>
          <li>
            • Changing the date here moves the unlock for everyone; the change takes effect as soon as
            you save.
          </li>
        </ul>
      </div>
    </Panel>;
}
function PreviewStat({ label, value }) {
  return <div className="rounded-lg border border-[rgba(233,168,242,.16)] py-3">
      <p className="font-display text-xl gold-text">{value}</p>
      <p className="text-[0.58rem] uppercase tracking-[0.18em] text-[color:var(--muted-foreground)]">
        {label}
      </p>
    </div>;
}
export {
  AdminSchedule as default
};
